// frontend/src/components/ListarOrdenesCompraModal.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import presupuestosService from '../services/presupuestosService';
import './ListarOrdenesCompraModal.css';

import Loading from './Loading';
const ListarOrdenesCompraModal = ({ recursoId, recursoNombre, onClose, onStatusChange }) => {
  const [ordenesCompra, setOrdenesCompra] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOCId, setExpandedOCId] = useState(null);
  const [cambiandoStatus, setCambiandoStatus] = useState({});

  useEffect(() => {
    const cargarDatos = async () => {
      setLoading(true);
      try {
        const response = await presupuestosService.getOrdenesCompraPorRecurso(recursoId);
        let ocData = [];

        if (response.data && response.data.results && Array.isArray(response.data.results)) {
          ocData = response.data.results;
        } else if (Array.isArray(response.data)) {
          ocData = response.data;
        }

        setOrdenesCompra(ocData);
      } catch (error) {
        console.error('Error al cargar órdenes de compra:', error);
        toast.error('Error al cargar órdenes de compra');
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, [recursoId]);


  const handleEnviarAEmitir = async (ocId) => {
    setCambiandoStatus(prev => ({
      ...prev,
      [ocId]: true
    }));

    try {
      const response = await presupuestosService.cambiarStatusOrdenCompra(ocId, 'enviada_emitir');

      // Actualizar la OC en la lista
      setOrdenesCompra(prev =>
        prev.map(oc => oc.id === ocId ? response.data : oc)
      );

      toast.success('Orden de compra enviada a emitir');

      // Llamar callback si existe
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      const errorMsg = error.response?.data?.error || 'Error al cambiar estado de la orden de compra';
      toast.error(errorMsg);
    } finally {
      setCambiandoStatus(prev => ({
        ...prev,
        [ocId]: false
      }));
    }
  };

  const handleMarcarEmitida = async (ocId) => {
    setCambiandoStatus(prev => ({
      ...prev,
      [ocId]: true
    }));

    try {
      const response = await presupuestosService.cambiarStatusOrdenCompra(ocId, 'emitida');

      // Actualizar la OC en la lista
      setOrdenesCompra(prev =>
        prev.map(oc => oc.id === ocId ? response.data : oc)
      );

      toast.success('Orden de compra marcada como emitida');

      // Llamar callback si existe para actualizar el consumo del presupuesto
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (error) {
      console.error('Error al marcar como emitida:', error);
      const errorMsg = error.response?.data?.error || 'Error al marcar la orden de compra como emitida';
      toast.error(errorMsg);
    } finally {
      setCambiandoStatus(prev => ({
        ...prev,
        [ocId]: false
      }));
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pendiente':
        return 'badge-pending';
      case 'enviada_emitir':
        return 'badge-sent';
      case 'emitida':
        return 'badge-issued';
      case 'cancelada':
        return 'badge-cancelled';
      default:
        return 'badge-default';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pendiente': 'Pendiente',
      'enviada_emitir': 'Enviada a Emitir',
      'emitida': 'Emitida',
      'cancelada': 'Cancelada'
    };
    return labels[status] || status;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content listar-oc-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Órdenes de Compra - {recursoNombre}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <Loading fullScreen={false} mensaje="Cargando órdenes de compra" />
        ) : ordenesCompra.length === 0 ? (
          <div className="empty-container">
            <p>No hay órdenes de compra para este recurso</p>
          </div>
        ) : (
          <div className="oc-list-container">
            {ordenesCompra.map(oc => (
              <div key={oc.id} className="oc-card">
                <div className="oc-card-header">
                  <div className="oc-basic-info">
                    <span className="oc-id">OC #{oc.id}</span>
                    <span className={`status-badge ${getStatusBadgeClass(oc.status)}`}>
                      {getStatusLabel(oc.status)}
                    </span>
                  </div>
                  <button
                    className="oc-expand-btn"
                    onClick={() => setExpandedOCId(expandedOCId === oc.id ? null : oc.id)}
                  >
                    {expandedOCId === oc.id ? '▼' : '▶'}
                  </button>
                </div>

                <div className="oc-main-info">
                  <div className="oc-info-item">
                    <span className="oc-label">Cantidad:</span>
                    <span className="oc-value">{oc.cantidad.toLocaleString('es-CL')} {oc.recurso_unidad || 'unidades'}</span>
                  </div>
                  <div className="oc-info-item">
                    <span className="oc-label">Valor Unitario:</span>
                    <span className="oc-value">${oc.valor_unitario.toLocaleString('es-CL')}</span>
                  </div>
                  <div className="oc-info-item oc-monto">
                    <span className="oc-label">Monto:</span>
                    <span className="oc-value">${oc.monto.toLocaleString('es-CL')}</span>
                  </div>
                </div>

                {expandedOCId === oc.id && (
                  <div className="oc-expanded-content">
                    {oc.proveedor && (
                      <div className="oc-proveedor-section">
                        <h4>📋 Información del Proveedor</h4>
                        <div className="info-grid">
                          {oc.proveedor.nombre_proveedor && (
                            <div className="info-item">
                              <span className="info-label">Proveedor:</span>
                              <span className="info-value">{oc.proveedor.nombre_proveedor}</span>
                            </div>
                          )}
                          {oc.proveedor.rut && (
                            <div className="info-item">
                              <span className="info-label">RUT:</span>
                              <span className="info-value">{oc.proveedor.rut}</span>
                            </div>
                          )}
                          {oc.proveedor.email && (
                            <div className="info-item">
                              <span className="info-label">Email:</span>
                              <span className="info-value">{oc.proveedor.email}</span>
                            </div>
                          )}
                          {oc.proveedor.mail && (
                            <div className="info-item">
                              <span className="info-label">Mail Alternativo:</span>
                              <span className="info-value">{oc.proveedor.mail}</span>
                            </div>
                          )}
                          {oc.proveedor.telefono && (
                            <div className="info-item">
                              <span className="info-label">Teléfono:</span>
                              <span className="info-value">{oc.proveedor.telefono}</span>
                            </div>
                          )}
                          {oc.proveedor.contacto && (
                            <div className="info-item">
                              <span className="info-label">Contacto:</span>
                              <span className="info-value">{oc.proveedor.contacto}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {oc.status === 'pendiente' && (
                      <div className="oc-actions">
                        <button
                          className="btn btn-primary"
                          onClick={() => handleEnviarAEmitir(oc.id)}
                          disabled={cambiandoStatus[oc.id]}
                        >
                          {cambiandoStatus[oc.id] ? 'Enviando...' : 'Enviar a Emitir OC'}
                        </button>
                      </div>
                    )}

                    {oc.status === 'enviada_emitir' && (
                      <div className="oc-actions">
                        <button
                          className="btn btn-success"
                          onClick={() => handleMarcarEmitida(oc.id)}
                          disabled={cambiandoStatus[oc.id]}
                        >
                          {cambiandoStatus[oc.id] ? 'Marcando...' : 'Marcar como Emitida'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListarOrdenesCompraModal;
